/**
 * Who the firm's clients are: who rides most, who pays most, who costs journeys, and who has
 * stopped ringing.
 *
 * The server sends every client once, most rides first, and this page cuts its own lists
 * from that — the top ten by rides, the top ten by spend, the ten who called off the most
 * cars — so that changing how many to show is a slice rather than another query over three
 * months of orders. The one figure that ignores the control is the top-ten share in the
 * tiles: it is always the top ten, so that it means the same thing on every visit.
 *
 * The cards come in pairs, and each pair is two equal columns of one height. A list with
 * three rows beside a list with ten would otherwise end a third of the way down, so every
 * list closes with a line that says what the rows shown add up to — the share of the
 * period's rides, money, lost journeys or unfilled orders — which is the figure a reader
 * wants anyway, and the loyalty spread is drawn as a donut so that the card has a shape
 * rather than five short rows.
 *
 * Everything here follows the day an order was *asked for*, as the demand and service reports
 * do. The shift book follows the day a ride *finished*, and the footnote says so, because a
 * ride across midnight at the edge of a month lands in different months on the two screens
 * and somebody will eventually compare them.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTenantFormats, type TenantFormats } from '../shell/formats';
import {
  Button,
  Card,
  CardHeader,
  Chip,
  ChipGroup,
  Count,
  EmptyState,
  Icon,
  Input,
  PageHeader,
  Pill,
  Segmented,
  StatTile,
  Sub,
  Table,
  Td,
  Th,
  Tr,
} from '../ui';
import { reportsApi, type ClientReport, type ClientRow, type LoyaltyBand } from './api';
import { ExportButton } from './ExportButton';
import { useReport, type ReportPageProps } from './frame';
import { PeriodRow } from './PeriodRow';

/** How many rows each top list shows. Zero is every one of them. */
const SIZES = [10, 25, 50, 0] as const;

export function ClientsPage({ frame }: ReportPageProps) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const [size, setSize] = useState<number>(10);

  const { data: report } = useReport(frame, (period) => reportsApi.clients(period));

  const top = <T,>(rows: T[]) => (size === 0 ? rows : rows.slice(0, size));

  // The four lists and the figures their closing lines are read against. Cut here once,
  // rather than inline, so the line under a list and the rows above it come from one slice.
  const rows = report?.rows ?? [];
  const byRides = top(rows.filter((row) => row.rides > 0));
  const bySpend = top(rows.filter((row) => row.spent > 0).sort((a, b) => b.spent - a.spent));
  const costly = top(
    rows.filter((row) => costOf(row) > 0).sort((a, b) => costOf(b) - costOf(a) || b.cancelled - a.cancelled),
  );
  const failed = top(rows.filter((row) => row.unfilled > 0).sort((a, b) => b.unfilled - a.unfilled));
  // Over the identified clients, as the server's top-ten shares are: an anonymous ride has
  // no client to be a share of, and it is already stated apart in the footnote.
  const totals = {
    rides: sum(rows, (row) => row.rides),
    spent: sum(rows, (row) => row.spent),
    costly: sum(rows, costOf),
    unfilled: sum(rows, (row) => row.unfilled),
  };

  return (
    <>
      <PageHeader
        title={t('nav.reports.clients')}
        description={t('reports.about.clients')}
        actions={<ExportButton frame={frame} path="/api/tenant/reports/clients" />}
      />

      <PeriodRow frame={frame} />

      {report === null ? (
        <Card>
          <EmptyState>{t('reports.loading')}</EmptyState>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-3">
            <Card>
              <StatTile
                label={t('reports.clients.clients')}
                value={formats.count(report.clients)}
                sub={t('reports.clients.ofOrders', { count: report.orders, shown: formats.count(report.orders) })}
              />
            </Card>
            <Card>
              <StatTile label={t('reports.clients.newClients')} value={formats.count(report.newClients)} />
            </Card>
            <Card>
              <StatTile
                label={t('reports.clients.returning')}
                value={formats.count(report.returningClients)}
              />
            </Card>
            <Card>
              {/* Always the top ten, whatever the chips below say: a share that moved when
                  somebody asked to see fifty rows would be a figure nobody could quote. */}
              <StatTile
                label={t('reports.clients.topTenShare')}
                value={formats.percent(report.topTenRideShare)}
                sub={
                  report.topTenSpendShare === null
                    ? undefined
                    : t('reports.clients.topTenSpendSub', {
                        share: formats.percent(report.topTenSpendShare),
                      })
                }
              />
            </Card>
          </div>

          <ChipGroup label={t('reports.clients.show')}>
            {SIZES.map((option) => (
              <Chip key={option} current={option === size} onClick={() => setSize(option)}>
                {option === 0 ? t('reports.clients.all') : formats.count(option)}
              </Chip>
            ))}
          </ChipGroup>

          <div className="grid gap-4 lg:grid-cols-2 items-stretch">
            <TopList
              title={t('reports.clients.byRides')}
              rows={byRides}
              value={(row) => row.rides}
              format={(row) => formats.count(row.rides)}
              empty={t('reports.clients.noClients')}
              summary={
                totals.rides > 0 &&
                t('reports.clients.summaryRides', {
                  shown: formats.count(byRides.length),
                  share: formats.percent(sum(byRides, (row) => row.rides) / totals.rides),
                })
              }
            />

            <TopList
              title={t('reports.clients.bySpend')}
              unit={report.currency}
              rows={bySpend}
              value={(row) => row.spent}
              format={(row) => formats.amount(row.spent)}
              empty={t('reports.clients.noClients')}
              summary={
                totals.spent > 0 &&
                t('reports.clients.summarySpend', {
                  shown: formats.count(bySpend.length),
                  share: formats.percent(sum(bySpend, (row) => row.spent) / totals.spent),
                })
              }
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2 items-stretch">
            <TopList
              title={t('reports.clients.costly')}
              hint={t('reports.clients.costlyHint')}
              rows={costly}
              value={costOf}
              format={(row) => formats.count(costOf(row))}
              detail={(row) =>
                t('reports.clients.costlyDetail', {
                  after: formats.count(row.cancelledAfterAssignment),
                  noShows: formats.count(row.noShows),
                  cancelled: formats.count(row.cancelled),
                })
              }
              empty={t('reports.clients.noCostly')}
              summary={
                totals.costly > 0 &&
                t('reports.clients.summaryCostly', {
                  shown: formats.count(costly.length),
                  share: formats.percent(sum(costly, costOf) / totals.costly),
                  total: formats.count(totals.costly),
                })
              }
            />

            <TopList
              title={t('reports.clients.failed')}
              hint={t('reports.clients.failedHint')}
              rows={failed}
              value={(row) => row.unfilled}
              format={(row) => formats.count(row.unfilled)}
              detail={(row) =>
                t('reports.clients.failedDetail', {
                  orders: formats.count(row.orders),
                  rides: formats.count(row.rides),
                })
              }
              empty={t('reports.clients.noFailed')}
              summary={
                totals.unfilled > 0 &&
                t('reports.clients.summaryFailed', {
                  shown: formats.count(failed.length),
                  share: formats.percent(sum(failed, (row) => row.unfilled) / totals.unfilled),
                  total: formats.count(totals.unfilled),
                })
              }
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2 items-stretch">
            <Lost report={report} formats={formats} limit={size} />
            <Loyalty bands={report.loyalty} formats={formats} />
          </div>

          <Everyone report={report} formats={formats} />

          <p className="text-secondary text-ink-3 max-w-[80ch] m-0">
            {t('reports.clients.attribution')}
            {report.anonymousOrders > 0 && (
              <>
                {' '}
                {t('reports.clients.anonymous', {
                  count: report.anonymousOrders,
                  shown: formats.count(report.anonymousOrders),
                })}
              </>
            )}
          </p>
        </>
      )}
    </>
  );
}

/** The cancellations that cost a journey: a car on its way called back, or arriving to nobody. */
function costOf(row: ClientRow): number {
  return row.cancelledAfterAssignment + row.noShows;
}

function sum(rows: ClientRow[], value: (row: ClientRow) => number): number {
  return rows.reduce((total, row) => total + value(row), 0);
}

/** A client's name over their number, or the number alone when nobody wrote a name down. */
function ClientName({ row }: { row: { displayName: string | null; phone: string | null } }) {
  const { t } = useTranslation();

  if (row.displayName === null || row.displayName.trim() === '') {
    return <span className="font-semibold">{row.phone ?? t('reports.clients.unknown')}</span>;
  }

  return (
    <>
      <span className="font-semibold">{row.displayName}</span>
      {row.phone !== null && <Sub block>{row.phone}</Sub>}
    </>
  );
}

/**
 * One ranked list: a place, a name, a bar against the first row, and the figure.
 *
 * The bar is scaled to the top of *this* list rather than to anything on the page, because
 * the question each list answers is "how far behind the first is the fifth", not "how do
 * rides compare with money".
 */
function TopList({
  title,
  hint,
  unit,
  rows,
  value,
  format,
  detail,
  empty,
  summary,
}: {
  title: string;
  hint?: string;
  unit?: string;
  rows: ClientRow[];
  value: (row: ClientRow) => number;
  format: (row: ClientRow) => string;
  detail?: (row: ClientRow) => ReactNode;
  empty: string;
  /** The line the list closes with: what the rows shown add up to. */
  summary?: ReactNode;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const most = rows.length === 0 ? 1 : Math.max(1, value(rows[0]));

  return (
    <Card className="min-w-0 h-full flex flex-col">
      <CardHeader title={unit === undefined ? title : `${title} (${unit})`}>
        <Count>{formats.count(rows.length)}</Count>
      </CardHeader>

      {hint !== undefined && <p className="text-secondary text-ink-3 px-3.5 pt-2.5 m-0">{hint}</p>}

      {rows.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState>{empty}</EmptyState>
        </div>
      ) : (
        <Table>
          <tbody>
            {rows.map((row, index) => (
              <Tr key={row.customerId}>
                <Td numeric className="w-[2.5rem] text-ink-3">
                  {formats.count(index + 1)}
                </Td>
                <Td header className="w-[40%]">
                  <ClientName row={row} />
                  {detail !== undefined && <Sub block>{detail(row)}</Sub>}
                </Td>
                <Td className="w-full">
                  <div
                    className="h-2 rounded-pill bg-surface-2"
                    role="img"
                    aria-label={t('reports.clients.barAlt', { value: format(row) })}
                  >
                    <div
                      className="h-2 rounded-pill bg-brand"
                      style={{ width: `${String(Math.max(2, (value(row) / most) * 100))}%` }}
                    />
                  </div>
                </Td>
                <Td numeric className="whitespace-nowrap font-semibold">
                  {format(row)}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Pinned to the foot of the card, so two lists of different lengths in one row
          close on the same line. */}
      {summary ? (
        <p className="text-secondary text-ink-3 px-3.5 py-2.5 mt-auto mb-0 border-t border-rule">
          {summary}
        </p>
      ) : null}
    </Card>
  );
}

/** Who rode in the previous stretch and has not ordered since. */
function Lost({
  report,
  formats,
  limit,
}: {
  report: ClientReport;
  formats: TenantFormats;
  limit: number;
}) {
  const { t } = useTranslation();

  const rows = limit === 0 ? report.lost : report.lost.slice(0, limit);

  return (
    <Card className="min-w-0 h-full flex flex-col">
      <CardHeader title={t('reports.clients.lost')}>
        <Count>{formats.count(report.lost.length)}</Count>
      </CardHeader>

      <p className="text-secondary text-ink-3 px-3.5 pt-2.5 m-0">
        {t('reports.clients.lostHint', {
          from: formats.dayLong(report.previousFrom),
          to: formats.dayLong(report.previousTo),
        })}
      </p>

      {rows.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState>{t('reports.clients.noLost')}</EmptyState>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th>{t('reports.clients.client')}</Th>
                <Th numeric>{t('reports.clients.ridesThen')}</Th>
                <Th numeric>
                  {t('reports.clients.spentThen')} ({report.currency})
                </Th>
                <Th numeric>{t('reports.clients.lastRide')}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Tr key={row.customerId}>
                  <Td header>
                    <ClientName row={row} />
                  </Td>
                  <Td numeric>{formats.count(row.rides)}</Td>
                  <Td numeric>{formats.amount(row.spent)}</Td>
                  <Td numeric>{formats.day(row.lastRide)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
}

/**
 * How many clients rode once, a few times, often — as a donut first, a table on request.
 *
 * The bands are an order of magnitude, so they are one hue stepped light to dark rather
 * than five unrelated colours: the darkest slice is the clients who rode most. The hue is
 * the firm's own, which is also why the steps are opacities over the card rather than five
 * fixed colours — a white-label firm keeps its chart in its colour in both themes. The
 * legend carries every band's label, count and share, so nothing here is read by colour
 * alone, and the table says the same figures in rows.
 */
function Loyalty({ bands, formats }: { bands: LoyaltyBand[]; formats: TenantFormats }) {
  const { t } = useTranslation();
  const [view, setView] = useState<'pie' | 'table'>('pie');

  const label = (band: LoyaltyBand) => {
    if (band.maxRides === null) {
      return t('reports.clients.bandOpen', { min: formats.count(band.minRides) });
    }

    if (band.minRides === 0) return t('reports.clients.bandNone');
    if (band.minRides === band.maxRides) return t('reports.clients.bandOne');

    return t('reports.clients.bandRange', {
      min: formats.count(band.minRides),
      max: formats.count(band.maxRides),
    });
  };

  // Most rides first, which is also darkest first, in the legend, the table and the ring.
  const ordered = [...bands].reverse();
  const total = ordered.reduce((count, band) => count + band.clients, 0);
  const shade = (index: number) => [1, 0.75, 0.52, 0.32, 0.15][index] ?? 0.15;

  return (
    <Card className="min-w-0 h-full flex flex-col">
      <CardHeader
        title={t('reports.clients.loyalty')}
        actions={
          <Segmented
            label={t('reports.clients.loyaltyViews')}
            value={view}
            onChange={setView}
            options={[
              { value: 'pie', label: t('reports.clients.viewPie') },
              { value: 'table', label: t('reports.clients.viewTable') },
            ]}
          />
        }
      >
        <Count>{formats.count(total)}</Count>
      </CardHeader>

      {total === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState>{t('reports.clients.noClients')}</EmptyState>
        </div>
      ) : view === 'table' ? (
        <Table>
          <thead>
            <tr>
              <Th>{t('reports.clients.ridesInPeriod')}</Th>
              <Th numeric>{t('reports.clients.clients')}</Th>
              <Th numeric>{t('reports.clients.share')}</Th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((band, index) => (
              <Tr key={band.minRides}>
                <Td header>
                  <span className="inline-flex items-center gap-2">
                    <Swatch opacity={shade(index)} />
                    {label(band)}
                  </span>
                </Td>
                <Td numeric>{formats.count(band.clients)}</Td>
                <Td numeric>{formats.percent(band.clients / total)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div className="flex-1 flex items-center gap-6 px-5 py-4 flex-wrap">
          <Donut
            slices={ordered.map((band, index) => ({
              key: String(band.minRides),
              value: band.clients,
              opacity: shade(index),
              title: t('reports.clients.slice', {
                band: label(band),
                count: formats.count(band.clients),
                share: formats.percent(band.clients / total),
              }),
            }))}
            alt={t('reports.clients.pieAlt')}
            centre={formats.count(total)}
            caption={t('reports.clients.clients')}
          />

          <ul className="flex-1 min-w-[200px] m-0 p-0 list-none flex flex-col gap-2">
            {ordered.map((band, index) => (
              <li key={band.minRides} className="flex items-center gap-2 text-[13px]">
                <Swatch opacity={shade(index)} />
                <span className="flex-1 min-w-0 truncate">{label(band)}</span>
                <span className="font-semibold tabular-nums">{formats.count(band.clients)}</span>
                <span className="text-ink-3 tabular-nums w-[4ch] text-right">
                  {formats.percent(band.clients / total)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/** One step of the ring's shade, as a 10px square beside its label. */
function Swatch({ opacity }: { opacity: number }) {
  return (
    <span
      aria-hidden
      className="inline-block w-2.5 h-2.5 rounded-[2px] bg-brand shrink-0"
      style={{ opacity }}
    />
  );
}

/**
 * A ring of slices with the total in the middle.
 *
 * Hand-drawn SVG, like the service report's bars: two arcs and a number do not need a
 * charting library. Each slice is outlined in the card's own surface, which is the 2px
 * gap that keeps two neighbouring shades apart without a line of a third colour.
 */
function Donut({
  slices,
  alt,
  centre,
  caption,
}: {
  slices: { key: string; value: number; opacity: number; title: string }[];
  alt: string;
  centre: string;
  caption: string;
}) {
  const total = slices.reduce((count, slice) => count + slice.value, 0);
  const radius = 44;
  const width = 22;
  const outer = radius + width / 2;
  const inner = radius - width / 2;
  const point = (r: number, a: number) => `${60 + r * Math.cos(a)} ${60 + r * Math.sin(a)}`;

  // Where each slice starts and ends, worked out once from the top of the ring.
  const arcs: { slice: (typeof slices)[number]; from: number; to: number }[] = [];

  for (const slice of slices.filter((candidate) => candidate.value > 0)) {
    const from = arcs.length === 0 ? -Math.PI / 2 : arcs[arcs.length - 1].to;

    arcs.push({ slice, from, to: from + (slice.value / total) * Math.PI * 2 });
  }

  return (
    <svg viewBox="0 0 120 120" role="img" aria-label={alt} className="w-[168px] h-[168px] shrink-0">
      {arcs.map(({ slice, from, to }) => {
        // A single slice is a full ring, which an arc command cannot draw: a path that
        // starts and ends on the same point has no length.
        if (slice.value === total) {
          return (
            <circle
              key={slice.key}
              cx={60}
              cy={60}
              r={radius}
              fill="none"
              className="stroke-brand"
              strokeWidth={width}
              style={{ opacity: slice.opacity }}
            >
              <title>{slice.title}</title>
            </circle>
          );
        }

        const large = to - from > Math.PI ? 1 : 0;

        const d = [
          `M ${point(outer, from)}`,
          `A ${outer} ${outer} 0 ${large} 1 ${point(outer, to)}`,
          `L ${point(inner, to)}`,
          `A ${inner} ${inner} 0 ${large} 0 ${point(inner, from)}`,
          'Z',
        ].join(' ');

        return (
          <path
            key={slice.key}
            d={d}
            className="fill-brand stroke-surface"
            strokeWidth={2}
            strokeLinejoin="round"
            style={{ fillOpacity: slice.opacity }}
          >
            <title>{slice.title}</title>
          </path>
        );
      })}

      <text x={60} y={58} textAnchor="middle" className="fill-ink text-[20px] font-semibold">
        {centre}
      </text>
      <text x={60} y={73} textAnchor="middle" className="fill-ink-3 text-[9px] uppercase tracking-wide">
        {caption}
      </text>
    </svg>
  );
}

type Column =
  | 'name'
  | 'orders'
  | 'rides'
  | 'cancelled'
  | 'unfilled'
  | 'spent'
  | 'averageFare'
  | 'customerSince'
  | 'lastRide';

interface Sort {
  column: Column;
  ascending: boolean;
}

/**
 * Every client in the period, sortable, with a box to find one.
 *
 * The total row is the sum of what is displayed, so a filtered table adds up to what is on
 * the screen; the tiles above are the whole period and do not move while somebody types.
 */
function Everyone({ report, formats }: { report: ClientReport; formats: TenantFormats }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState<Sort>({ column: 'rides', ascending: false });

  const rows = useMemo(() => {
    const needle = filter.trim().toLocaleLowerCase();

    const visible = report.rows.filter(
      (row) =>
        needle.length === 0 ||
        (row.displayName ?? '').toLocaleLowerCase().includes(needle) ||
        (row.phone ?? '').includes(needle),
    );

    const direction = sort.ascending ? 1 : -1;

    return [...visible].sort((left, right) => direction * compare(left, right, sort.column));
  }, [report.rows, filter, sort]);

  const shown = useMemo(
    () => ({
      orders: rows.reduce((sum, row) => sum + row.orders, 0),
      rides: rows.reduce((sum, row) => sum + row.rides, 0),
      spent: rows.reduce((sum, row) => sum + row.spent, 0),
    }),
    [rows],
  );

  const app = (row: ClientRow) => (row.sources.RiderApp ?? 0) + (row.sources.Web ?? 0);

  return (
    <Card className="min-w-0">
      <CardHeader
        title={t('reports.clients.everyone')}
        actions={
          <Input
            type="search"
            className="w-[220px]"
            placeholder={t('reports.clients.filterHint')}
            aria-label={t('reports.clients.filter')}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        }
      >
        <Count>{formats.count(report.rows.length)}</Count>
      </CardHeader>

      {report.rows.length === 0 ? (
        <EmptyState>{t('reports.clients.noClients')}</EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <Table tight>
            <thead>
              <tr>
                <Sortable column="name" sort={sort} onSort={setSort}>
                  {t('reports.clients.client')}
                </Sortable>
                <Sortable column="orders" sort={sort} onSort={setSort} numeric>
                  {t('reports.clients.orders')}
                </Sortable>
                <Sortable column="rides" sort={sort} onSort={setSort} numeric>
                  {t('reports.clients.rides')}
                </Sortable>
                <Sortable column="cancelled" sort={sort} onSort={setSort} numeric>
                  {t('reports.clients.cancelled')}
                </Sortable>
                <Sortable column="unfilled" sort={sort} onSort={setSort} numeric>
                  {t('reports.clients.unfilled')}
                </Sortable>
                <Sortable column="spent" sort={sort} onSort={setSort} numeric>
                  {t('reports.clients.spent')} ({report.currency})
                </Sortable>
                <Sortable column="averageFare" sort={sort} onSort={setSort} numeric>
                  {t('reports.clients.averageFare')}
                </Sortable>
                <Th numeric>{t('reports.clients.payment')}</Th>
                <Th numeric>{t('reports.clients.source')}</Th>
                <Sortable column="customerSince" sort={sort} onSort={setSort} numeric>
                  {t('reports.clients.since')}
                </Sortable>
                <Sortable column="lastRide" sort={sort} onSort={setSort} numeric>
                  {t('reports.clients.lastRide')}
                </Sortable>
                <Th>
                  <span className="sr-only">{t('reports.clients.journeys')}</span>
                </Th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => (
                <Tr key={row.customerId}>
                  <Td header>
                    <ClientName row={row} />
                  </Td>
                  <Td numeric>{formats.count(row.orders)}</Td>
                  <Td numeric>
                    {formats.count(row.rides)}
                    {row.unpricedRides > 0 && (
                      <span title={t('reports.shiftBook.unpricedHint')}>
                        {' '}
                        <Sub>{t('reports.shiftBook.unpriced', { count: row.unpricedRides })}</Sub>
                      </span>
                    )}
                  </Td>
                  <Td numeric className={costOf(row) > 0 ? 'text-warn' : undefined}>
                    {formats.count(row.cancelled + row.noShows)}
                    {costOf(row) > 0 && (
                      <Sub block>
                        {t('reports.clients.costlyDetail', {
                          after: formats.count(row.cancelledAfterAssignment),
                          noShows: formats.count(row.noShows),
                          cancelled: formats.count(row.cancelled),
                        })}
                      </Sub>
                    )}
                  </Td>
                  <Td numeric className={row.unfilled > 0 ? 'text-warn' : 'text-ink-3'}>
                    {formats.count(row.unfilled)}
                  </Td>
                  <Td numeric className="font-semibold">
                    {formats.amount(row.spent)}
                  </Td>
                  <Td numeric>{row.averageFare === null ? '—' : formats.amount(row.averageFare)}</Td>
                  <Td numeric className="whitespace-nowrap">
                    {t('reports.clients.paymentSplit', {
                      cash: formats.amount(row.cash),
                      card: formats.amount(row.card),
                      account: formats.amount(row.account),
                    })}
                    {row.unrecorded > 0 && (
                      <Sub block>
                        {t('reports.clients.unrecorded', { amount: formats.amount(row.unrecorded) })}
                      </Sub>
                    )}
                  </Td>
                  <Td numeric className="whitespace-nowrap">
                    {t('reports.clients.sourceSplit', {
                      phone: formats.count(row.sources.Phone ?? 0),
                      app: formats.count(app(row)),
                    })}
                  </Td>
                  <Td numeric className="whitespace-nowrap">
                    {formats.day(row.customerSince)}
                    {row.isNew && (
                      <>
                        {' '}
                        <Pill tone="info" soft>
                          {t('reports.clients.new')}
                        </Pill>
                      </>
                    )}
                  </Td>
                  <Td numeric className="whitespace-nowrap">
                    {row.lastRide === null ? '—' : formats.day(row.lastRide)}
                  </Td>
                  <Td>
                    <Button
                      icon
                      size="sm"
                      title={t('reports.clients.journeys')}
                      aria-label={t('reports.clients.journeysOf', { name: row.displayName ?? row.phone ?? '' })}
                      onClick={() => navigate(`/reports/journeys?customer=${row.customerId}`)}
                    >
                      <Icon name="route" small />
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>

            <tfoot>
              <tr className="font-semibold">
                <Td header>{t('reports.total', { count: rows.length })}</Td>
                <Td numeric>{formats.count(shown.orders)}</Td>
                <Td numeric>{formats.count(shown.rides)}</Td>
                <Td />
                <Td />
                <Td numeric>{formats.amount(shown.spent)}</Td>
                <Td />
                <Td />
                <Td />
                <Td />
                <Td />
                <Td />
              </tr>
            </tfoot>
          </Table>
        </div>
      )}
    </Card>
  );
}

function Sortable({
  column,
  sort,
  onSort,
  numeric,
  children,
}: {
  column: Column;
  sort: Sort;
  onSort: (next: Sort) => void;
  numeric?: boolean;
  children: ReactNode;
}) {
  const current = sort.column === column;

  return (
    <Th
      numeric={numeric}
      scope="col"
      aria-sort={current ? (sort.ascending ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 cursor-pointer hover:text-ink"
        // A first click on a new column sorts it the way somebody means it: names upward,
        // numbers downward. Anything else makes every column need two clicks.
        onClick={() => onSort({ column, ascending: current ? !sort.ascending : column === 'name' })}
      >
        {children}
        {current && <Icon name={sort.ascending ? 'arrow-up' : 'arrow-down'} small />}
      </button>
    </Th>
  );
}

function compare(left: ClientRow, right: ClientRow, column: Column): number {
  switch (column) {
    case 'name':
      return (left.displayName ?? left.phone ?? '').localeCompare(
        right.displayName ?? right.phone ?? '',
      );
    case 'cancelled':
      return left.cancelled + left.noShows - (right.cancelled + right.noShows);
    case 'averageFare':
      return (left.averageFare ?? -1) - (right.averageFare ?? -1);
    case 'customerSince':
      return left.customerSince.localeCompare(right.customerSince);
    case 'lastRide':
      return (left.lastRide ?? '').localeCompare(right.lastRide ?? '');
    default:
      return left[column] - right[column];
  }
}
